import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { type Endereco } from '@/lib/endereco';
import { LIMITE_TEXTO, mascaraCep } from '@/lib/mascaras';
import { useCatalogo, type Estado } from '@/lib/catalogo';

// Bloco de endereço da aplicação.
//
// Construção única: todo formulário que pede endereço usa este componente. Isso
// mantém o mesmo comportamento de CEP, a mesma ordem de campos e a mesma lista
// de estados em toda a aplicação — e faz uma correção aqui valer em todas as
// telas.
//
// A busca de CEP e a lista de estados vêm do backend (funções `cep` e
// `config`), que respondem do cache.

interface Props {
  valor: Endereco;
  onChange: (endereco: Endereco) => void;
  /** O comentário é opcional: nem todo cadastro de endereço quer uma descrição. */
  comComentario?: boolean;
  rotuloComentario?: string;
  desabilitado?: boolean;
  idPrefixo?: string;
}

const CLASSE =
  'w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 disabled:opacity-60';

export default function CampoEndereco({
  valor,
  onChange,
  comComentario = false,
  rotuloComentario = 'Comentário',
  desabilitado = false,
  idPrefixo = 'endereco',
}: Props) {
  const { itens: estados } = useCatalogo<Estado>('estados');
  const [buscando, setBuscando] = useState(false);
  const [avisoCep, setAvisoCep] = useState<string | null>(null);

  // A consulta de CEP é assíncrona e o usuário continua digitando enquanto ela
  // corre. Sem esta referência, o resultado voltaria com o estado capturado no
  // início da busca e apagaria o que foi digitado no meio-tempo.
  const valorRef = useRef(valor);
  valorRef.current = valor;

  // Evita repetir a consulta do mesmo CEP a cada blur.
  const ultimoCepBuscado = useRef<string>('');

  const alterar = (campo: keyof Endereco, novo: string) => {
    onChange({ ...valor, [campo]: novo });
  };

  const buscarCep = async (cepDigitado: string) => {
    const digitos = cepDigitado.replace(/\D/g, '');
    if (digitos.length !== 8 || digitos === ultimoCepBuscado.current) return;

    ultimoCepBuscado.current = digitos;
    setBuscando(true);
    setAvisoCep(null);

    const { data, error } = await supabase.functions.invoke(`cep?cep=${digitos}`, {
      method: 'GET',
    });
    setBuscando(false);

    const resposta = data as (Endereco & { error?: string }) | null;

    if (!resposta || resposta.error) {
      // Falha na consulta não impede o cadastro: o endereço continua editável.
      setAvisoCep(resposta?.error || error?.message || 'Não foi possível consultar o CEP.');
      return;
    }

    // Parte do estado atual, não do capturado quando a busca começou.
    const atual = valorRef.current;
    onChange({
      ...atual,
      cep: mascaraCep(resposta.cep),
      logradouro: resposta.logradouro || atual.logradouro,
      bairro: resposta.bairro || atual.bairro,
      cidade: resposta.cidade || atual.cidade,
      estado: resposta.estado || atual.estado,
      // O complemento do CEP costuma ser faixa de numeração ("de 612 a 1510"),
      // não o complemento do imóvel: preserva o que a pessoa digitou.
      complemento: atual.complemento,
    });
  };

  const id = (campo: string) => `${idPrefixo}_${campo}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
      <div className="md:col-span-2">
        <label htmlFor={id('cep')} className="block text-sm font-medium text-foreground-700 mb-1.5">
          CEP
        </label>
        <div className="relative">
          <input
            id={id('cep')}
            type="text"
            inputMode="numeric"
            value={valor.cep}
            disabled={desabilitado}
            onChange={(e) => {
              const formatado = mascaraCep(e.target.value);
              alterar('cep', formatado);
              const digitos = formatado.replace(/\D/g, '');
              if (digitos.length < 8) ultimoCepBuscado.current = '';
              if (digitos.length === 8) void buscarCep(formatado);
            }}
            onBlur={(e) => void buscarCep(e.target.value)}
            placeholder="00000-000"
            className={CLASSE}
          />
          {buscando ? (
            <i className="ri-loader-4-line animate-spin absolute right-3 top-3 text-foreground-400"></i>
          ) : null}
        </div>
        {avisoCep ? <p className="text-xs text-amber-600 mt-1">{avisoCep}</p> : null}
      </div>

      <div className="md:col-span-3">
        <label
          htmlFor={id('logradouro')}
          className="block text-sm font-medium text-foreground-700 mb-1.5"
        >
          Endereço
        </label>
        <input
          id={id('logradouro')}
          type="text"
          maxLength={LIMITE_TEXTO}
          value={valor.logradouro}
          disabled={desabilitado}
          onChange={(e) => alterar('logradouro', e.target.value)}
          placeholder="Rua, avenida, alameda"
          className={CLASSE}
        />
      </div>

      <div className="md:col-span-1">
        <label
          htmlFor={id('numero')}
          className="block text-sm font-medium text-foreground-700 mb-1.5"
        >
          Número
        </label>
        {/* Texto, não número: "s/n", "120A" e "Km 3" são endereços válidos. */}
        <input
          id={id('numero')}
          type="text"
          maxLength={LIMITE_TEXTO}
          value={valor.numero}
          disabled={desabilitado}
          onChange={(e) => alterar('numero', e.target.value)}
          placeholder="120 ou s/n"
          className={CLASSE}
        />
      </div>

      <div className="md:col-span-2">
        <label
          htmlFor={id('bairro')}
          className="block text-sm font-medium text-foreground-700 mb-1.5"
        >
          Bairro
        </label>
        <input
          id={id('bairro')}
          type="text"
          maxLength={LIMITE_TEXTO}
          value={valor.bairro}
          disabled={desabilitado}
          onChange={(e) => alterar('bairro', e.target.value)}
          className={CLASSE}
        />
      </div>

      <div className="md:col-span-2">
        <label
          htmlFor={id('cidade')}
          className="block text-sm font-medium text-foreground-700 mb-1.5"
        >
          Cidade
        </label>
        <input
          id={id('cidade')}
          type="text"
          maxLength={LIMITE_TEXTO}
          value={valor.cidade}
          disabled={desabilitado}
          onChange={(e) => alterar('cidade', e.target.value)}
          className={CLASSE}
        />
      </div>

      <div className="md:col-span-2">
        <label
          htmlFor={id('estado')}
          className="block text-sm font-medium text-foreground-700 mb-1.5"
        >
          Estado
        </label>
        <select
          id={id('estado')}
          value={valor.estado}
          disabled={desabilitado}
          onChange={(e) => alterar('estado', e.target.value)}
          className={CLASSE}
        >
          <option value="">Selecione...</option>
          {estados.map((estado) => (
            <option key={estado.uf} value={estado.uf}>
              {estado.nome} ({estado.uf})
            </option>
          ))}
        </select>
      </div>

      <div className="md:col-span-6">
        <label
          htmlFor={id('complemento')}
          className="block text-sm font-medium text-foreground-700 mb-1.5"
        >
          Complemento
          <span className="font-normal text-foreground-500"> — bloco, torre, sala, referência</span>
        </label>
        <input
          id={id('complemento')}
          type="text"
          maxLength={LIMITE_TEXTO}
          value={valor.complemento}
          disabled={desabilitado}
          onChange={(e) => alterar('complemento', e.target.value)}
          placeholder="Ex: Bloco B, próximo à praça"
          className={CLASSE}
        />
      </div>

      {comComentario ? (
        <div className="md:col-span-6">
          <label
            htmlFor={id('comentario')}
            className="block text-sm font-medium text-foreground-700 mb-1.5"
          >
            {rotuloComentario}
          </label>
          <textarea
            id={id('comentario')}
            rows={3}
            value={valor.comentario ?? ''}
            disabled={desabilitado}
            onChange={(e) => alterar('comentario', e.target.value)}
            placeholder="Descrição livre sobre o local"
            className={CLASSE}
          />
        </div>
      ) : null}
    </div>
  );
}
